import './Theatre.scss';
import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import FoyerProvider from '@/components/edifice/Foyer/FoyerProvider';
import FoyerShell from '../Foyer/FoyerShell';
import Foyer from '@/components/edifice/Foyer/Foyer';
import Edifice from '@/components/edifice/Edifice/Edifice';
import Exit from '@/components/edifice/Exit/Exit';
import Marquee from '@/components/edifice/Marquee/Marquee';
import PrerenderGate from '@/utils/prerender/PrerenderGate';
import { useGetNavQuery } from '@/generated/graphql';
import { markNavReady, isPrerenderRun, filterNavigationData } from '@/utils';

const EditPromptGuard = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/provider/RootEditProvider/EditPromptGuard'
        ),
);
const BrowserNavigationBlocker = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/provider/RootEditProvider/BrowserNavigationBlocker'
        ),
);
const LeaveAppBlocker = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/provider/RootEditProvider/LeaveAppBlocker'
        ),
);

export type PlaceholderDataAttributes =
    | {
          'date-placeholder-type-nodeId': string;
          'date-placeholder-adminTitle': string;
          'date-placeholder-adminNote'?: string;
          'date-placeholder-divider'?: string;
      }
    | object;

export const getPlaceholderDataAttributes = (
    isPlaceholder,
    type,
    nodeId,
    adminTitle,
    adminNote,
) => {
    const placeholderDataAttributes = {};
    if (isPlaceholder) {
        placeholderDataAttributes['date-placeholder--type-node-id'] =
            `${type}Id: ${nodeId}`;
        placeholderDataAttributes['date-placeholder--admin-title'] =
            `AdminTitle: ${adminTitle}`;
        if (adminNote) {
            placeholderDataAttributes['date-placeholder--admin-note'] =
                `\n\nAdminNote:\n${adminNote}`;
        }
    }
    return placeholderDataAttributes;
};

export default function Theatre() {
    const { userCurrent } = useUserCurrent();
    const isContentEditor = !!userCurrent?.isContentEditor;
    const location = useLocation();

    const { data, error } = useGetNavQuery({
        variables: { isContentEditor },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
    });
    const navReady = !!data && !error;

    useEffect(() => {
        if (!isPrerenderRun()) return;
        if (navReady) markNavReady();
    }, [navReady]);

    const realms = useMemo(() => {
        return filterNavigationData(data?.realms?.items ?? [], userCurrent);
    }, [data, userCurrent]);

    if (error) {
        return <pre>Error...{JSON.stringify(error, null, 2)}</pre>;
    }

    return (
        <Edifice>
            {userCurrent?.isEditMode ? (
                <Suspense fallback={null}>
                    <EditPromptGuard />
                    <BrowserNavigationBlocker />
                    <LeaveAppBlocker />
                </Suspense>
            ) : null}

            <PrerenderGate navReady={navReady} />

            <FoyerProvider
                initialNav={navReady ? { realms: realms, location } : undefined}
            >
                <FoyerShell
                    navReady={navReady}
                    navRealms={realms}
                    location={location}
                >
                    <div className="Theatre_entrance">
                        <Marquee />
                        <Foyer />
                        <main
                            id="main-content"
                            className={classNames('Theatre', {
                                isEditMode: userCurrent?.isEditMode,
                            })}
                        >
                            <Outlet />
                        </main>
                    </div>
                </FoyerShell>
            </FoyerProvider>
            <Exit />
        </Edifice>
    );
}
